import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Cart} from '../../../model/cart/Cart';
import {Address} from '../../../model/Address';
import {AddressService} from '../../service-data/address-service';
import {AddressValidationService, ValidationAddress} from '../../service-data/address-validation.service';
import {StartupService} from '../../service-ui/startup.service';
import {COUNTRIES} from '../../util/countries';
import {US_STATES} from '../../util/us-states';

/**
 * Delivery-address section of the checkout accordion.
 *
 * Extracted from `CartPageComponent`. Owns the saved-address radio list,
 * the inline address form, address-validation UI, and the "save this
 * address to your address book" prompt. The parent retains the readiness
 * flags (`addressReady`) and the cross-cutting `checkAddressIsComplete()`
 * orchestrator — this component emits change events the parent listens
 * for so it can re-run that orchestrator.
 *
 * Carries the `legacy-controls` host class to match `CartPageComponent`
 * per the "Modern controls" convention in `AGENTS.md`.
 */
@Component({
  selector: 'app-cart-address-section',
  templateUrl: './cart-address-section.component.html',
  styleUrls: ['../cart-page/checkout-shared.css', './cart-address-section.component.css']
})
export class CartAddressSectionComponent implements OnChanges {
  protected readonly Number = Number;
  readonly countries = COUNTRIES;
  readonly usStates = US_STATES;

  @Input() cart: Cart;
  @Input() addresses: Address[] = [];
  @Input() addressReady: boolean = false;
  @Input() userControlledAccordion: boolean = false;

  // Two-way: parent owns the actual edit state because the place-order
  // flow checks it from outside the section.
  @Input() editDeliveryAddress: boolean = false;
  @Output() editDeliveryAddressChange = new EventEmitter<boolean>();

  // Two-way: parent reads this to gate the "save changes" button enable
  // in legacy code; preserved for symmetry.
  @Input() deliveryAddressChanged: boolean = false;
  @Output() deliveryAddressChangedChange = new EventEmitter<boolean>();

  // Two-way: the validated/recommended address surfaced by the SmartyStreets
  // (or equivalent) service is rendered both here and consumed by the parent
  // for the final order-placement payload.
  @Input() validatedAddress: ValidationAddress;
  @Output() validatedAddressChange = new EventEmitter<ValidationAddress>();

  @Output() userControlledAccordionChange = new EventEmitter<boolean>();

  /** Emitted after any address field change so the parent re-runs
   *  `checkAddressIsComplete()` (which drives `addressReady`,
   *  `paymentReady`, `shippingReady`, and the new-shipping-flow kickoff). */
  @Output() addressInfoChanged = new EventEmitter<void>();

  /** Emitted when a saved address is removed from the address book so
   *  the parent can resync its `addresses` array. */
  @Output() addressesChange = new EventEmitter<Address[]>();

  constructor(
    private addressService: AddressService,
    private addressValidationService: AddressValidationService,
    public startupService: StartupService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['cart']) {
      this.normalizeFulfillmentOptions();
    }
  }

  /**
   * True when the "Payee" / "Inventory Type" selectors should be shown.
   * Only a user who is also a ServiceProvider or a Network gets to
   * choose: an ordinary Organization always pays for its own order and
   * always prints to order. Single source of truth shared with the
   * template so the controls and the cart normalization can never
   * disagree.
   */
  get canChooseFulfillmentOptions(): boolean {
    return this.startupService.canChooseFulfillmentOptions();
  }

  /**
   * Force the Organization defaults (Payee = Organization, Inventory
   * Type = Print To Order) whenever the current user isn't allowed to
   * choose. Without this a cart carrying a service-provider payer or a
   * FULFILL_FROM_INVENTORY inventory type — restored from a previous
   * session, or left over from a role switch — would keep those values
   * all the way into `placeOrder` while the controls that could correct
   * them are hidden.
   */
  private normalizeFulfillmentOptions() {
    if (!this.cart) { return; }
    if (this.canChooseFulfillmentOptions) { return; }
    if (this.cart.applyDefaultFulfillmentOptions()) {
      console.log('CartAddressSectionComponent: reset payer / inventory type to the Organization defaults');
    }
  }

  /** Material icon name for the section header. */
  addressSectionIcon(): string {
    if (!this.cart) { return 'location_on'; }
    if (this.cart.addressStatus === 'VALIDATED' || this.cart.addressStatus === 'MANUAL_OVERRIDE') { return 'check_circle'; }
    if (this.cart.addressStatus === 'VALIDATING') { return 'hourglass_empty'; }
    return 'location_on';
  }

  /** State-based css class for the section header icon. */
  addressSectionClass(): string {
    if (!this.cart) { return 'section-not-ready'; }
    if (this.cart.addressStatus === 'VALIDATED' || this.cart.addressStatus === 'MANUAL_OVERRIDE') { return 'section-ready'; }
    if (this.cart.addressStatus === 'VALIDATING') { return 'section-computing'; }
    return 'section-not-ready';
  }

  onAccordionToggle() {
    this.userControlledAccordion = true;
    this.userControlledAccordionChange.emit(true);
  }

  onFieldChange() {
    this.deliveryAddressChanged = true;
    this.deliveryAddressChangedChange.emit(true);
    this.addressInfoChanged.emit();
  }

  /** Field-change handler that does NOT trigger `checkAddressIsComplete`.
   *  Used for optional metadata fields like Company / Apt where filling in
   *  the field shouldn't flip the address-ready bit on its own. */
  onMetadataFieldChange() {
    this.deliveryAddressChanged = true;
    this.deliveryAddressChangedChange.emit(true);
  }

  startEditingNewAddress() {
    this.editDeliveryAddress = true;
    this.editDeliveryAddressChange.emit(true);
    this.cart.setAddress(undefined);
    this.cart.address.org_ID = Number(this.startupService.selectedRoleId);
  }

  startEditingSavedAddress(address: Address) {
    this.cart.setAddress(address);
    this.editDeliveryAddress = true;
    this.editDeliveryAddressChange.emit(true);
  }

  cancelEditing() {
    this.editDeliveryAddress = false;
    this.editDeliveryAddressChange.emit(false);
  }

  selectSavedAddress(address: Address) {
    this.cart.setAddress(address);
    this.addressInfoChanged.emit();
  }

  /** Atomically set this address as the org default. */
  makeAddressDefault(address: Address) {
    for (const currentAddress of this.addressService.objects) {
      if (currentAddress.default) {
        currentAddress['updatingState'] = 'UPDATING';
        this.addressService.updateItem(address).subscribe(_o => {
          delete address['updatingState'];
        });
      }
    }
    address.default = true;
    address['updatingState'] = 'UPDATING';
    if (address.ID > 0) {
      this.addressService.updateItem(address).subscribe(_o => {
        delete address['updatingState'];
      });
    }
  }

  deleteSavedDeliveryAddress(address: Address) {
    if (address.ID > 0) {
      address['updatingState'] = 'UPDATING';
      this.addressService.deleteItem(address).subscribe(_o => {
        const index = this.addresses.indexOf(address);
        if (index >= 0) { this.addresses.splice(index, 1); }
        delete address['updatingState'];
        this.addressesChange.emit(this.addresses);
      });
    }
  }

  saveChangesToDeliveryAddress(address: Address) {
    if (address.ID > 0) {
      address['updatingState'] = 'UPDATING';
      this.addressService.updateItem(address).subscribe(_o => {
        delete address['updatingState'];
      });
    } else {
      address.org_ID = Number(this.startupService.selectedRoleId);
      this.addressService.addItem(address).subscribe(o => {
        this.addresses.push(o);
        address['updatingState'] = 'UPDATING';
        address.ID = o.ID;
        this.addressesChange.emit(this.addresses);
      });
    }
  }

  async validateCartAddress() {
    const address = new ValidationAddress(undefined);
    address.title = '';
    address.firstname = this.cart.address.firstname;
    address.lastname = this.cart.address.lastname;
    address.company = this.cart.address.company;
    address.address1 = this.cart.address.address1;
    address.address2 = this.cart.address.address2;
    address.address3 = '';
    address.address4 = '';
    address.city = this.cart.address.city;
    address.state = this.cart.address.state;
    address.country = this.cart.address.country;
    address.postalcode = this.cart.address.postalcode;
    if (address.country === 'US') {
      address.country = 'USA';
    }

    this.addressValidationService.validate(address).subscribe(addressValidationResult => {
      if (addressValidationResult) {
        const outcome = addressValidationResult.getOutcome();
        if (outcome === 'ERROR') {
          // The call did not complete. Nothing is known about this address, so
          // it must not be reported as unrecognised.
          this.cart.addressValidationStatus = 'VALIDATION_ERROR';
        } else if (outcome === 'UNSUPPORTED_COUNTRY') {
          // No validator covers this country. Offering "update the address and
          // re-validate" here sends the customer round a loop that cannot
          // terminate.
          this.cart.addressValidationStatus = 'VALIDATION_UNAVAILABLE';
        } else if (addressValidationResult.recommendedAddresses.length > 0) {
          this.validatedAddress = new ValidationAddress(address);
          this.validatedAddress.company = addressValidationResult.recommendedAddresses[0].company
            ? addressValidationResult.recommendedAddresses[0].company
            : address.company;
          this.validatedAddress.address1 = addressValidationResult.recommendedAddresses[0].address1
            ? addressValidationResult.recommendedAddresses[0].address1
            : '';
          this.validatedAddress.address2 = addressValidationResult.recommendedAddresses[0].address2
            ? addressValidationResult.recommendedAddresses[0].address2
            : '';
          this.validatedAddress.city = addressValidationResult.recommendedAddresses[0].city;
          this.validatedAddress.state = addressValidationResult.recommendedAddresses[0].state;
          this.validatedAddress.postalcode = addressValidationResult.recommendedAddresses[0].postalcode;
          this.validatedAddress.country = addressValidationResult.recommendedAddresses[0].country;
          this.validatedAddressChange.emit(this.validatedAddress);
          this.cart.addressValidationStatus = 'SUCCESS';
        } else {
          this.cart.addressValidationStatus = 'NO VALID SUGGESTIONS';
        }
      } else {
        console.log(`Error validating address`);
      }
    });
  }

  /** Apply the validated address back into the cart and mark complete. */
  applyValidatedAddress() {
    if (!this.validatedAddress) { return; }
    this.cart.address.address1 = this.validatedAddress.address1;
    this.cart.address.address2 = this.validatedAddress.address2;
    this.cart.address.city = this.validatedAddress.city;
    this.cart.address.postalcode = this.validatedAddress.postalcode;
    this.cart.address.state = this.validatedAddress.state;
    this.cart.address.country = (this.validatedAddress.country === 'USA' ? 'US' : this.validatedAddress.country);
    this.cart.address.validated = true;
    this.cart.address.override = false;
    this.cart.addressValidationStatus = 'VALIDATED';
    // Mirror `addressValidationStatus` onto `addressStatus`. The two
    // are kept in sync by `Cart.setAddress` on the saved-address path,
    // but this direct-mutation flow bypassed that helper and left
    // `addressStatus` stuck at its prior value (typically `'ENTERED'`
    // or `'PENDING'`). Downstream gates that check `addressStatus`
    // (`cart-page.maybeAutoComputeShipping` and `maybeComputeSalesTax`)
    // then refused to run, so shipping options weren't computed and
    // sales tax wasn't triggered even though `addressReady` (which
    // reads `addressValidationStatus`) had flipped true — the user saw
    // the address section as complete but no shipping rates ever
    // appeared (bug 2026-07-22 pass 4).
    this.cart.addressStatus = 'VALIDATED';
    this.deliveryAddressChanged = true;
    this.deliveryAddressChangedChange.emit(true);
    this.addressInfoChanged.emit();
  }

  /** Bypass the validation suggestion and use the user-entered address as-is. */
  overrideAddressValidation() {
    this.cart.address.override = true;
    this.cart.addressValidationStatus = 'MANUAL_OVERRIDE';
    // Same mirroring as `applyValidatedAddress` — see that method's
    // block comment. Without this, `maybeAutoComputeShipping` /
    // `maybeComputeSalesTax` skip their work when the user chooses
    // "Use entered address" past a validation warning.
    this.cart.addressStatus = 'MANUAL_OVERRIDE';
    this.addressInfoChanged.emit();
  }

  /** Re-trigger validation, then re-check completeness via the parent. */
  validateAndCheck() {
    this.validateCartAddress();
    this.addressInfoChanged.emit();
  }

  /** True when the current cart address looks like a freshly entered
   *  address that is not yet in the user's address book. */
  shouldOfferToSaveAddress(): boolean {
    if (!this.cart || !this.cart.address) { return false; }
    if (this.cart.address.ID && this.cart.address.ID > 0) { return false; }
    if (this.cart.addressStatus !== 'VALIDATED' && this.cart.addressStatus !== 'MANUAL_OVERRIDE') { return false; }
    if (!this.cart.address.address1 || !this.cart.address.city) { return false; }
    return true;
  }

  saveCurrentAddressToBook() {
    if (!this.cart || !this.cart.address) { return; }
    const addr = this.cart.address;
    addr.org_ID = Number(this.startupService.selectedRoleId);
    this.addressService.addItem(addr).subscribe(saved => {
      if (saved) {
        addr.ID = saved.ID;
        this.addresses.push(saved);
        this.addressesChange.emit(this.addresses);
      }
    });
  }

  /** Payee-mode and inventory-type dropdowns sit inside the address
   *  section in the existing layout; their changes need to recompute
   *  costs+shipping just like the legacy handler did. */
  onPayerOrInventoryTypeChange() {
    this.addressInfoChanged.emit();
  }
}
